import { useEffect, useMemo, useState } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import analyticsByLineQuery, {
	type useAnalyticsByLineQuery,
} from '../components/AnalyticsByLine/__generated__/useAnalyticsByLineQuery.graphql.ts';
import analyticsContentQuery, {
	type AnalyticsDialogAnalyticsContentQuery,
} from '../components/AnalyticsDialog/__generated__/AnalyticsDialogAnalyticsContentQuery.graphql.ts';
import { useIsPrivacyModeEligible } from '../components/common/useIsPrivacyModeEligible.tsx';

export type PageViewsAccess = {
	canView: boolean;
	isPrivacyModeEnabled: boolean;
	loading: boolean;
	error: Error | undefined;
};

/** Reuses existing Analytics queries when the preview panel needs viewer access. */
export const usePageViewsAccess = (contentId: string, skip = false): PageViewsAccess => {
	const { cloudId, edition } = useSessionData();
	const environment = useRelayEnvironment();
	const isPrivacyModeEligible = useIsPrivacyModeEligible();
	const enabled = !skip && !!contentId && !!cloudId && edition === ConfluenceEdition.PREMIUM;
	const request = useMemo(
		() => ({ contentId, cloudId, enabled, isPrivacyModeEligible, environment }),
		[contentId, cloudId, enabled, isPrivacyModeEligible, environment],
	);
	const [result, setResult] = useState<{ request: typeof request; access: PageViewsAccess }>();

	useEffect(() => {
		if (!request.enabled) {
			return;
		}
		let permission: useAnalyticsByLineQuery['response'] | undefined;
		let privacy: AnalyticsDialogAnalyticsContentQuery['response'] | undefined;
		let error: Error | undefined;
		const publish = () => {
			const isPrivacyModeEnabled =
				request.isPrivacyModeEligible && privacy?.privacyMode?.isPrivacyModeEnabled !== false;
			const loading = !error && (!permission || (request.isPrivacyModeEligible && !privacy));
			setResult({
				request,
				access: {
					canView:
						!loading &&
						!error &&
						!isPrivacyModeEnabled &&
						permission?.user?.confluence?.permissionType === 'INTERNAL',
					isPrivacyModeEnabled,
					loading,
					error,
				},
			});
		};
		const onError = (queryError: Error) => {
			error = queryError;
			publish();
		};
		const permissionSubscription = fetchQuery<useAnalyticsByLineQuery>(
			request.environment,
			analyticsByLineQuery,
			{ contentId: request.contentId },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				permission = data;
				publish();
			},
			error: onError,
		});
		const privacySubscription = request.isPrivacyModeEligible
			? fetchQuery<AnalyticsDialogAnalyticsContentQuery>(
					request.environment,
					analyticsContentQuery,
					{
						contentId: request.contentId,
						cloudId: request.cloudId,
						isPrivacyModeGateEnabled: true,
					},
					{ fetchPolicy: 'store-or-network' },
				).subscribe({
					next: (data) => {
						privacy = data;
						publish();
					},
					error: onError,
				})
			: undefined;

		return () => {
			permissionSubscription.unsubscribe();
			privacySubscription?.unsubscribe();
		};
	}, [request]);

	// Previous-page access must not enable viewer fetching while the next request starts.
	return enabled && result?.request === request
		? result.access
		: {
				canView: false,
				isPrivacyModeEnabled: isPrivacyModeEligible,
				loading: enabled,
				error: undefined,
			};
};
